import { memo, useCallback, useMemo, useState } from 'react'
import { z } from 'zod'
import { type Node, type NodeProps, Position } from '@xyflow/react'
import { AlertTriangle } from 'lucide-react'
import { DynamicIcon } from 'lucide-react/dynamic'
import { useShallow } from 'zustand/shallow'
import { useFlowStore } from '@/stores/flow-store'
import CustomHandle from '../../handles/custom-handler'
import { BuilderNode, RegisterNodeMetadata } from '../types'
import { getNodeDetail } from '../utils'

const StartNodeDataSchema = z.object({
  label: z.string().optional(),
  deletable: z.boolean().optional(),
})

export type StartNodeData = z.infer<typeof StartNodeDataSchema>

const NODE_TYPE = BuilderNode.START

export type StartNodeProps = NodeProps<Node<StartNodeData, typeof NODE_TYPE>>

export function StartNode({
  id,
  data,
  selected,
  isConnectable,
}: StartNodeProps) {
  const meta = useMemo(() => getNodeDetail(NODE_TYPE), [])
  const { showNodePropertiesOf } = useFlowStore(
    useShallow((s) => ({
      showNodePropertiesOf: s.actions.sidebar.showNodePropertiesOf,
    }))
  )
  const {
    actions: { validation: validationActions },
  } = useFlowStore()
  const hasErrors = validationActions.getNodeErrors(id).length > 0

  const [sourceHandleId] = useState<string>(`${id}-source`)

  const handleShowNodeProperties = useCallback(() => {
    showNodePropertiesOf({ id, type: NODE_TYPE })
  }, [id, showNodePropertiesOf])

  return (
    <div
      data-selected={selected}
      onDoubleClick={handleShowNodeProperties}
      className={`relative flex cursor-pointer items-center rounded-full border bg-card px-4 py-2 text-foreground shadow-sm transition data-[selected=true]:border-primary ${
        hasErrors
          ? 'border-red-500/50 shadow-red-500/20'
          : 'border-card-foreground/10'
      }`}
    >
      {hasErrors && (
        <div className='absolute z-10 -right-1 -top-1'>
          <div className='flex items-center justify-center w-5 h-5 bg-red-500 rounded-full'>
            <AlertTriangle className='w-3 h-3 text-white' />
          </div>
        </div>
      )}
      <DynamicIcon
        name={meta.icon}
        className={'scale-130 mr-2 size-4 shrink-0'}
      />

      <span className='mr-1'>{data.label || meta.title}</span>
      <CustomHandle
        type='source'
        id={sourceHandleId}
        position={Position.Right}
        isConnectable={isConnectable}
      />
    </div>
  )
}

export const metadata: RegisterNodeMetadata<StartNodeData> = {
  type: NODE_TYPE,
  node: memo(StartNode),
  detail: {
    icon: 'play',
    title: 'Start',
    description: 'Start the chatbot flow',
  },
  connection: {
    inputs: 0,
    outputs: 1,
  },
  available: true,
  defaultData: {
    label: 'Start',
    deletable: false,
  },
  validationSchema: StartNodeDataSchema,
}
